import { filter, isUndefined, uniq } from 'lodash-es';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithReset, useAtomCallback, useResetAtom } from 'jotai/utils';
import { useIsOnlyOneAssistant } from 'hooks/business/isOnlyOneAssistant';
import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { defaultBatchTransferMaxLimit } from 'services/config/app';
import { useFlagConfigValue } from '../system/useFlagConfig';
import { useGetConversationById } from '../lruConversation';

const batchForwardEnableState = atom(false);
const batchForwardConvIdsState = atomWithReset<string[]>([]);
const batchForwardIsDelConvState = atom<boolean>(false);
const batchForwardLoadingState = atom<boolean>(false);

export const useBatchForwardLoading = () => useAtomValue(batchForwardLoadingState);
export const useSetBatchForwardLoading = () => useSetAtom(batchForwardLoadingState);

export const useBatchForwardEnable = () => useAtomValue(batchForwardEnableState);
export const useSetBatchForwardEnable = () => useSetAtom(batchForwardEnableState);

export const useBatchForwardIsDelConv = () => useAtomValue(batchForwardIsDelConvState);
export const useSetBatchForwardIsDelConv = () => useSetAtom(batchForwardIsDelConvState);

export const useBatchForwardConvIds = () => useAtomValue(batchForwardConvIdsState);

export const useSetBatchForwardConvIds = () => {
  const { batchTransferMaxLimit = defaultBatchTransferMaxLimit } = useFlagConfigValue();
  const { t } = useTranslate();

  return useAtomCallback(
    useCallback(
      async (get, set, conv: string | string[], ops?: 'add' | 'del') => {
        const olds = get(batchForwardConvIdsState);
        let convs: string[] = [];
        if (!conv) return;
        if (Array.isArray(conv)) {
          convs = conv;
        } else if (typeof conv === 'string' && conv) {
          convs = [conv];
        }

        if (ops === 'del') {
          set(
            batchForwardConvIdsState,
            filter(olds, (id) => !convs.includes(id)),
          );
        } else {
          const newConvs = uniq([...olds, ...convs]);
          if (newConvs.length > batchTransferMaxLimit) {
            message.warning(t('forward.batchLimitWarning', { maxLimit: batchTransferMaxLimit }));
            set(batchForwardConvIdsState, newConvs.splice(0, batchTransferMaxLimit));
          } else {
            set(batchForwardConvIdsState, newConvs);
          }
        }
      },
      [batchTransferMaxLimit, t],
    ),
  );
};

export const useResetBatchForwardConvIds = () => {
  const reset = useResetAtom(batchForwardConvIdsState);
  return useAtomCallback(
    useCallback(
      async (_, set, convs?: string[]) => {
        if (isUndefined(convs)) {
          reset();
        } else {
          set(batchForwardConvIdsState, uniq(convs));
        }
      },
      [reset],
    ),
  );
};

export const useConvCanNotForward = () => {
  const isOnlyOneAssistant = useIsOnlyOneAssistant();
  const getConversation = useGetConversationById();

  const func = useCallback(
    (conversationId: string) => {
      const conv = getConversation(conversationId);
      return conv && (isOnlyOneAssistant || conv.blacklistFlag);
    },
    [isOnlyOneAssistant, getConversation],
  );

  return func;
};
